import { v4 as uuidv4 } from 'uuid';
import ErrorHandler from './errorHandler';

// Unified storage interface for easy localStorage → PostgreSQL migration
class OracleDataManager {
  constructor(storageType = 'localStorage') {
    this.storage = storageType === 'localStorage' 
      ? new LocalStorageAdapter() 
      : new PostgreSQLAdapter();
  }

  async saveOracleSession(sessionData) {
    try {
      return await this.storage.save(sessionData);
    } catch (error) {
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.DATABASE,
        `Failed to save oracle session: ${error.message}`,
        sessionData.userId,
        { sessionId: sessionData.sessionId, errorStack: error.stack }
      );
      throw error;
    }
  }

  async getOracleSession(sessionId, userId = null) {
    try {
      return await this.storage.get(sessionId, userId);
    } catch (error) {
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.DATABASE,
        `Failed to get oracle session: ${error.message}`,
        userId,
        { sessionId, errorStack: error.stack }
      );
      throw error;
    }
  }

  async getUserOracleHistory(userId) {
    try {
      return await this.storage.getHistory(userId);
    } catch (error) {
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.DATABASE,
        `Failed to get user oracle history: ${error.message}`,
        userId,
        { errorStack: error.stack }
      );
      throw error;
    }
  }

  async updateOracleSession(sessionId, updates) {
    try {
      return await this.storage.update(sessionId, updates);
    } catch (error) {
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.DATABASE,
        `Failed to update oracle session: ${error.message}`,
        null,
        { sessionId, updates, errorStack: error.stack }
      );
      throw error;
    }
  }

  async deleteOracleSession(sessionId) {
    try {
      return await this.storage.delete(sessionId);
    } catch (error) {
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.DATABASE,
        `Failed to delete oracle session: ${error.message}`,
        null,
        { sessionId, errorStack: error.stack }
      );
      throw error;
    }
  }
}

// Phase 1A: Local storage for UI development
class LocalStorageAdapter {
  async save(sessionData) {
    try {
      const key = `oracle_${sessionData.sessionId}`;
      const dataToSave = {
        ...sessionData,
        savedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      localStorage.setItem(key, JSON.stringify(dataToSave));
      return sessionData.sessionId;
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        throw new Error('Storage quota exceeded - please clear old oracle sessions');
      }
      throw new Error(`LocalStorage save failed: ${error.message}`);
    }
  }

  async get(sessionId, userId = null) {
    try {
      const key = `oracle_${sessionId}`;
      const data = localStorage.getItem(key);
      if (!data) return null;

      const session = JSON.parse(data);
      // Ensure user can only access their own sessions when userId is provided
      if (userId && session.userId !== userId) {
        return null;
      }

      return session;
    } catch (error) {
      throw new Error(`LocalStorage get failed: ${error.message}`);
    }
  }

  async update(sessionId, updates) {
    try {
      const existing = await this.get(sessionId);
      if (!existing) {
        throw new Error(`Oracle session ${sessionId} not found`);
      }

      const updated = {
        ...existing,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      const key = `oracle_${sessionId}`;
      localStorage.setItem(key, JSON.stringify(updated));
      return updated;
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        throw new Error('Storage quota exceeded - please clear old oracle sessions');
      }
      if (error.message.includes('not found')) {
        throw error; // Re-throw session not found errors
      }
      throw new Error(`LocalStorage update failed: ${error.message}`);
    }
  }

  async delete(sessionId) {
    try {
      const key = `oracle_${sessionId}`;
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      throw new Error(`LocalStorage delete failed: ${error.message}`);
    }
  }

  async getHistory(userId = 'local_user') {
    try {
      const sessions = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('oracle_')) {
          try {
            const session = JSON.parse(localStorage.getItem(key));
            // Filter sessions by userId to ensure proper user isolation
            if (session && session.isComplete && session.userId === userId) {
              sessions.push({
                sessionId: session.sessionId,
                createdAt: session.savedAt || session.timestamp,
                themeId: session.themeId,
                userQuestion: session.userQuestion,
                isComplete: session.isComplete,
                completedAt: session.completedAt
              });
            }
          } catch (parseError) {
            // Skip corrupted session entries
            console.warn(`Skipping corrupted oracle session: ${key}`, parseError);
            continue;
          }
        }
      }
      return sessions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } catch (error) {
      throw new Error(`LocalStorage getHistory failed: ${error.message}`);
    }
  }
}

// Placeholder for Phase 1B: PostgreSQL implementation
class PostgreSQLAdapter {
  constructor() {
    // Will be implemented when migrating to backend
    console.warn('PostgreSQL adapter not implemented yet');
  }

  async save(sessionData) {
    throw new Error('PostgreSQL adapter not implemented');
  }

  async get(sessionId, userId) {
    throw new Error('PostgreSQL adapter not implemented');
  }

  async update(sessionId, updates) {
    throw new Error('PostgreSQL adapter not implemented');
  }

  async delete(sessionId) {
    throw new Error('PostgreSQL adapter not implemented');
  }

  async getHistory(userId) {
    throw new Error('PostgreSQL adapter not implemented');
  }
}

// Oracle session factory
export const createOracleSession = (userId = 'local_user') => {
  return {
    sessionId: uuidv4(),
    userId,
    timestamp: new Date().toISOString(),
    
    // Theme Selection (Hidden in Phase 1)
    gender: 'unisex',
    category: 'none',
    themeId: 'basic', // Always derived from gender + category
    
    // Oracle Variables
    raySelection: null,    // Variable [A] - 1 of 12 rays
    userQuestion: '',      // Variable [B] - Text input
    diagnosisType: null,   // Variable [C] - 1 of 9 types
    diagnosisElement: null, // Variable [D] - 1 of 4 elements
    solutionType: null,    // Variable [E] - 1 of 9 types  
    solutionElement: null, // Variable [F] - 1 of 4 elements
    
    // Generated Oracle Content (Claude API outputs)
    blockageText: '',      // Generated from [A], [B], [C], [D]
    unblockText: '',       // Generated from [A], [B], [E], [F]
    wisdomText: '',        // Generated from [A], [E], [F]
    decreeText: '',        // Generated from [A], [B], [E], [F]
    
    // Session Status
    currentStep: 6,        // Start from ray selection (Step 6)
    isComplete: false,
    completedAt: null
  };
};

// Theme determination logic (ready for Phase 2)
const oracleThemeMatrix = {
  'man+love': 'masculine-heart',
  'man+finance': 'masculine-wealth', 
  'man+purpose': 'masculine-purpose',
  'man+none': 'masculine-neutral',
  
  'woman+love': 'feminine-heart',
  'woman+finance': 'feminine-wealth',
  'woman+purpose': 'feminine-purpose', 
  'woman+none': 'feminine-neutral',
  
  'unisex+love': 'universal-heart',
  'unisex+finance': 'universal-wealth',
  'unisex+purpose': 'universal-purpose',
  'unisex+none': 'basic' // MVP result
};

export const determineOracleTheme = (genderChoice, categoryChoice) => {
  const themeKey = `${genderChoice}+${categoryChoice}`;
  const selectedTheme = oracleThemeMatrix[themeKey];
  
  if (!selectedTheme) {
    console.error(`Invalid theme combination: ${themeKey}`);
    return 'basic'; // Fallback only for invalid combinations
  }
  
  return selectedTheme;
};

// Initialize singleton instance
const oracleDataManager = new OracleDataManager('localStorage');

export default oracleDataManager;